'use client'

import React, { FC, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import classNames from 'classnames'
import { useTheme } from 'next-themes'
import BitmapCanvas, { type BitmapStatus } from './bitmap-canvas'
import { webgl2Supported } from './gl-renderer'
import type { BitmapLayout } from './layout-cache'
import { useBitmap3d } from './bitmap-3d'
import BitmapPlaceholder from './bitmap-placeholder'

export { default as BitmapCanvas } from './bitmap-canvas'
export type { BitmapStatus } from './bitmap-canvas'

/** Parse "840000.bitmap" (or a bare number) to a block height. */
export function parseBitmapHeight(name?: string | null): number | null {
	if (!name) return null
	const m = String(name)
		.trim()
		.match(/^(\d+)(?:\.bitmap)?$/i)
	if (!m) return null
	const n = Number(m[1])
	return Number.isFinite(n) ? n : null
}

// ── Quality tiers ───────────────────────────────────────────────────────────
// A grid of animated tiles is expensive. Each tile watches its own frame rate
// and steps down (full → reduced → canvas2d → static) when it drops below 24
// fps for a while, and back up when it recovers.

type Tier = 'full' | 'reduced' | 'canvas2d' | 'static'
const TIERS: Tier[] = ['full', 'reduced', 'canvas2d', 'static']

class FrameMonitor {
	private times: number[] = []
	private last = 0
	private low = 0
	private high = 0
	tier: Tier
	private readonly downgradeFrames: number
	private readonly upgradeFrames: number

	constructor(tier: Tier, mobile: boolean) {
		this.tier = tier
		this.downgradeFrames = mobile ? 10 : 20
		this.upgradeFrames = mobile ? 40 : 60
	}

	/** Returns true when the tier changed. */
	record(now: number): boolean {
		if (this.tier === 'static') return false
		if (this.last > 0) {
			this.times.push(now - this.last)
			if (this.times.length > 30) this.times.shift()
		}
		this.last = now
		if (this.times.length < 10) return false
		const fps = 1000 / (this.times.reduce((a, b) => a + b, 0) / this.times.length)
		if (fps < 24) {
			this.low++
			this.high = 0
			if (this.low >= this.downgradeFrames) {
				this.low = 0
				return this.step(1)
			}
		} else if (fps > 50) {
			this.high++
			this.low = 0
			if (this.high >= this.upgradeFrames) {
				this.high = 0
				return this.step(-1)
			}
		} else {
			this.low = 0
			this.high = 0
		}
		return false
	}

	private step(dir: 1 | -1): boolean {
		const i = TIERS.indexOf(this.tier) + dir
		if (i < 0 || i >= TIERS.length) return false
		this.tier = TIERS[i]
		this.times = []
		return true
	}
}

/** Hydration-safe media query: false on the server, live on the client. */
function useMedia(query: string): boolean {
	const subscribe = useCallback(
		(onChange: () => void) => {
			const mq = window.matchMedia(query)
			mq.addEventListener('change', onChange)
			return () => mq.removeEventListener('change', onChange)
		},
		[query]
	)
	return useSyncExternalStore(
		subscribe,
		() => window.matchMedia(query).matches,
		() => false
	)
}

const noSubscribe = () => () => {}
/** WebGL2 availability: false on the server, detected once on the client. */
function useWebgl2(): boolean {
	return useSyncExternalStore(noSubscribe, webgl2Supported, () => false)
}

/**
 * The nearest ancestor that actually scrolls. The collection grid lives in an
 * `overflow-y: auto` panel, and an IntersectionObserver rooted at the viewport
 * clips its rootMargin to that panel's box, so "near the viewport" degenerated
 * to "inside the panel": tiles were painted complete off-screen, then wiped
 * and re-animated the instant they scrolled in. Rooting at the scroller makes
 * the margins mean what they say.
 */
function findScrollRoot(el: Element | null): Element | null {
	let node = el?.parentElement ?? null
	while (node && node !== document.body) {
		const { overflowY } = getComputedStyle(node)
		if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
			return node
		}
		node = node.parentElement
	}
	return null
}

function useInView(options: { threshold?: number; rootMargin?: string; once?: boolean }) {
	const { threshold = 0.1, rootMargin = '100px', once = true } = options
	const [inView, setInView] = useState(false)
	const seenRef = useRef(false)
	const observerRef = useRef<IntersectionObserver | null>(null)
	const ref = useCallback(
		(el: Element | null) => {
			observerRef.current?.disconnect()
			observerRef.current = null
			if (!el || (once && seenRef.current)) return
			const io = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						setInView(entry.isIntersecting)
						if (entry.isIntersecting) {
							seenRef.current = true
							if (once) {
								io.disconnect()
								observerRef.current = null
							}
						}
					}
				},
				{ threshold, rootMargin, root: findScrollRoot(el) }
			)
			io.observe(el)
			observerRef.current = io
		},
		[threshold, rootMargin, once]
	)
	useEffect(() => () => observerRef.current?.disconnect(), [])
	return { ref, inView }
}

/**
 * Card tilt: the tile follows the cursor with a small perspective rotation,
 * springing flat when it leaves. Disabled for touch pointers.
 */
export function useTilt(maxDeg = 8) {
	const coarse = useMedia('(pointer: coarse)')
	const [tilt, setTilt] = useState({ x: 0, y: 0 })
	const onMouseMove = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (coarse) return
			const r = e.currentTarget.getBoundingClientRect()
			const px = e.clientX - r.left
			const py = e.clientY - r.top
			setTilt({
				x: -((py - r.height / 2) / (r.height / 2)) * maxDeg,
				y: ((px - r.width / 2) / (r.width / 2)) * maxDeg
			})
		},
		[coarse, maxDeg]
	)
	const onMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), [])
	const style: React.CSSProperties = coarse
		? { transition: 'transform 0.15s cubic-bezier(0.2, 0, 0, 1), border-color 0.2s ease' }
		: {
				transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
				transition: 'transform 0.1s ease-out, border-color 0.2s ease'
		  }
	return { onMouseMove, onMouseLeave, style }
}

// Snapshots encode a whole canvas to WebP. Run them one at a time: when a
// grid's tiles finish their fly-in together, back-to-back encodes blocked the
// main thread for seconds (#errors MainThreadFreeze on /collection/bitmap).
let snapshotQueue: Promise<void> = Promise.resolve()

function snapshot(
	canvas: HTMLCanvasElement,
	mobile: boolean,
	previous: React.MutableRefObject<string | null>,
	set: (u: string) => void
) {
	snapshotQueue = snapshotQueue.then(
		() =>
			new Promise<void>((done) => {
				try {
					// toBlob encodes off the main thread; toDataURL does not.
					if (typeof canvas.toBlob === 'function') {
						canvas.toBlob(
							(blob) => {
								if (blob) {
									if (previous.current?.startsWith('blob:')) URL.revokeObjectURL(previous.current)
									const url = URL.createObjectURL(blob)
									previous.current = url
									set(url)
								}
								done()
							},
							'image/webp',
							mobile ? 0.65 : 0.8
						)
						return
					}
					const url = canvas.toDataURL('image/webp', 0.8)
					previous.current = url
					set(url)
				} catch {
					// tainted or unsupported; keep the live canvas
				}
				done()
			})
	)
}

export type BitmapArtProps = {
	/** Block height, or a name like "840000.bitmap". */
	height: number | string
	/** Design size of the tile in CSS px; the backing store scales by DPR. */
	canvasSize?: number
	/** Cursor repulsion + glow after the fly-in. */
	interactive?: boolean
	className?: string
	/** Called when the block finishes loading (tx count, time). */
	onLayout?: (l: BitmapLayout) => void
	/** Shown when the block is not available yet. */
	missingLabel?: string
	/** Show the block's transaction count in the corner once it has loaded. */
	showTxCount?: boolean
	/** Force 2D/3D; defaults to the viewer's saved preference. */
	isometric?: boolean
}

/**
 * An animated bitmap tile. Idle → shimmer while the block loads → squares fly
 * in → interactive. Once settled and scrolled far off screen it is swapped for
 * a WebP snapshot so a long grid does not keep hundreds of canvases alive.
 */
const BitmapArtTile: FC<BitmapArtProps> = ({
	height,
	canvasSize = 300,
	interactive = true,
	className,
	onLayout,
	missingLabel = 'Bitmap not found',
	showTxCount = false,
	isometric: isometricProp
}) => {
	const [preferred3d] = useBitmap3d()
	const isometric = isometricProp ?? preferred3d
	const blockHeight = typeof height === 'number' ? height : parseBitmapHeight(height)
	const isMobile = useMedia('(max-width: 767px)')
	const isTablet = useMedia('(min-width: 768px) and (max-width: 1023px)')
	const reducedMotion = useMedia('(prefers-reduced-motion: reduce)')
	const webgl = useWebgl2()
	const initialTier: Tier = reducedMotion
		? 'static'
		: isMobile || isTablet
		? webgl
			? 'reduced'
			: 'canvas2d'
		: webgl
		? 'full'
		: 'canvas2d'
	const maxDpr = isMobile ? 2 : isTablet ? 2.5 : undefined

	const [status, setStatus] = useState<BitmapStatus>('idle')
	// The frame-rate watchdog may move a tile off its initial tier.
	const [tierOverride, setTierOverride] = useState<Tier | null>(null)
	const tier = tierOverride ?? initialTier
	const monitorRef = useRef<FrameMonitor | null>(null)
	const hostRef = useRef<HTMLDivElement | null>(null)
	const snapshotRef = useRef<string | null>(null)
	const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null)
	const [parked, setParked] = useState(false)
	// True once the fly-in has played and a snapshot exists; a re-mounted canvas
	// (waking from parked) then draws the finished bitmap without replaying.
	const [settled, setSettled] = useState(false)

	const { ref: entryRef, inView: entryInView } = useInView({ threshold: 0.2, once: true })
	const { ref: nearRef, inView: near } = useInView({
		once: false,
		rootMargin: '200% 0px',
		threshold: 0
	})
	const setRefs = useCallback(
		(el: HTMLDivElement | null) => {
			hostRef.current = el
			entryRef(el)
			nearRef(el)
		},
		[entryRef, nearRef]
	)

	// Frame-rate watchdog, only while the tile is actually drawing (the fly-in
	// window). A permanent rAF per tile kept 64 callbacks alive for the life
	// of the page and read scroll jank as "this tile is slow".
	useEffect(() => {
		if (status !== 'done' || parked || settled) return
		const m = monitorRef.current ?? (monitorRef.current = new FrameMonitor(tier, isMobile))
		let raf = 0
		const until = performance.now() + 3500
		const tick = (now: number) => {
			if (m.record(now)) setTierOverride(m.tier)
			if (m.tier !== 'static' && now < until) raf = requestAnimationFrame(tick)
		}
		raf = requestAnimationFrame(tick)
		return () => cancelAnimationFrame(raf)
	}, [status, parked, tier, isMobile, settled])

	// Snapshot 3.5s after the fly-in finishes (or immediately once static).
	useEffect(() => {
		if (status !== 'done' || parked) return
		const delay = tier === 'static' ? 0 : 3500
		let idle = 0
		const t = setTimeout(() => {
			const run = () => {
				const canvas = hostRef.current?.querySelector('canvas')
				if (canvas) {
					snapshot(canvas, isMobile, snapshotRef, setSnapshotUrl)
					setSettled(true)
				}
			}
			// Encoding is synchronous; let each tile take a free slice instead
			// of every tile encoding in the same frame.
			if (typeof requestIdleCallback === 'function') {
				idle = requestIdleCallback(run, { timeout: 2000 })
			} else {
				run()
			}
		}, delay)
		return () => {
			clearTimeout(t)
			if (idle && typeof cancelIdleCallback === 'function') cancelIdleCallback(idle)
		}
	}, [status, parked, tier, isMobile])

	// Retake the snapshot once a 2D⇄3D transition has settled.
	useEffect(() => {
		if (status !== 'done' || parked || !snapshotRef.current) return
		const t = setTimeout(() => {
			const canvas = hostRef.current?.querySelector('canvas')
			if (canvas) snapshot(canvas, isMobile, snapshotRef, setSnapshotUrl)
		}, 2000)
		return () => clearTimeout(t)
	}, [isometric, status, parked, isMobile])

	// Park (swap to the snapshot) when far off screen; wake when near again.
	useEffect(() => {
		if (status !== 'done' || !snapshotRef.current) return
		setParked(!near)
	}, [near, status])

	useEffect(
		() => () => {
			if (snapshotRef.current?.startsWith('blob:')) URL.revokeObjectURL(snapshotRef.current)
		},
		[]
	)

	const handleStatus = useCallback((s: BitmapStatus) => setStatus(s), [])
	const [txCount, setTxCount] = useState<number | null>(null)
	const handleLayout = useCallback(
		(l: BitmapLayout) => {
			setTxCount(l.count)
			onLayout?.(l)
		},
		[onLayout]
	)
	const showSnapshot = parked && !!snapshotUrl
	const skipEntry = settled

	if (blockHeight === null) {
		return (
			<div className={classNames('relative aspect-square overflow-hidden bg-white dark:bg-[#090c11]', className)}>
				<div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-zinc-600">
					{missingLabel}
				</div>
			</div>
		)
	}

	return (
		<div
			ref={setRefs}
			className={classNames('relative aspect-square overflow-hidden bg-white dark:bg-[#090c11]', className)}
			style={{ viewTransitionName: `bitmap-${blockHeight}` } as React.CSSProperties}
			data-bitmap-height={blockHeight}
		>
			{snapshotUrl && (
				<img
					src={snapshotUrl}
					alt={`Bitmap ${blockHeight}`}
					draggable={false}
					style={{ imageRendering: 'pixelated', width: '100%', height: '100%' }}
					className={classNames(
						'absolute inset-0 block',
						showSnapshot || status !== 'done'
							? 'opacity-100'
							: 'opacity-0 transition-opacity duration-500'
					)}
				/>
			)}
			{!showSnapshot && (
				<div
					className={classNames(
						'absolute inset-0 transition-opacity duration-500',
						status === 'done' ? 'opacity-100' : 'opacity-0'
					)}
				>
					{tier === 'static' && snapshotUrl ? (
						<img
							src={snapshotUrl}
							alt={`Bitmap ${blockHeight}`}
							draggable={false}
							style={{ imageRendering: 'pixelated', width: '100%', height: '100%' }}
							className="block"
						/>
					) : (
						<BitmapCanvas
							height={blockHeight}
							canvasSize={canvasSize}
							renderer={tier === 'canvas2d' || tier === 'static' ? 'canvas2d' : 'webgl'}
							enableRepulsion={interactive && tier === 'full'}
							inView={entryInView}
							skipEntryAnimation={skipEntry || tier === 'static'}
							isometric={isometric && tier !== 'canvas2d' && tier !== 'static'}
							maxDpr={maxDpr}
							mobileMode={isMobile}
							onStatus={handleStatus}
							onLayout={handleLayout}
						/>
					)}
				</div>
			)}
			{showTxCount && txCount !== null && status === 'done' && (
				<span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-transparent px-1.5 py-0.5 font-mono text-[10px] leading-none text-black dark:bg-black/60 dark:text-white/70">
					{txCount.toLocaleString('en-US')} txs
				</span>
			)}
			{(status === 'loading' || status === 'idle') && !showSnapshot && (
				<BitmapPlaceholder seed={blockHeight} count={9} className="bg-white dark:bg-[#090c11]" />
			)}
			{(status === 'error' || status === 'missing') && (
				<div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-[#090c11]">
					<span className="px-3 text-center font-mono text-[10px] text-zinc-600">
						{status === 'missing' ? missingLabel : 'Failed to render bitmap'}
					</span>
				</div>
			)}
		</div>
	)
}

// Theme-specific snapshots must never be reused after switching appearance.
const BitmapArt: FC<BitmapArtProps> = (props) => {
 const { resolvedTheme } = useTheme()
 return <BitmapArtTile key={resolvedTheme || 'light'} {...props} />
}
export default BitmapArt
